import { useCallback, useMemo, useState } from 'react';
import type { IncidentGateConfig, HoldStrategy } from '../../types/pipelineConfig';
import { StepConfigHeader } from './StepConfigHeader';
import { StrategySelector } from './StrategySelector';
import type { StrategyOption } from './StrategySelector';
import { TimeWindowInput } from './TimeWindowInput';
import { getDefaultIncidentGateConfig } from '../../utils/pipelineDefaults';

interface IncidentGateConfigEditorProps {
  config?: IncidentGateConfig;
  onChange: (config: IncidentGateConfig) => void;
  endpointType: string | number;
  disabled?: boolean;
  isSystemDefault?: boolean;
  hideToggle?: boolean;
}

const HOLD_STRATEGY_OPTIONS: StrategyOption<HoldStrategy>[] = [
  {
    value: 'hold_for_resolve',
    label: 'Hold for Resolve',
    icon: '⏸️',
    description: 'Hold alert for X minutes. Cancel if a resolved event arrives before the timer expires.',
  },
  {
    value: 'occurrence_only',
    label: 'Occurrence Threshold',
    icon: '🔢',
    description: 'Only create an incident after N occurrences within a time window.',
  },
  {
    value: 'both',
    label: 'Combined',
    icon: '🔀',
    description: 'Hold for resolve AND require occurrence threshold. Both conditions apply.',
  },
];

function FieldLabel({ label, helper, htmlFor }: { label: string; helper?: string; htmlFor?: string }) {
  return (
    <div className="mb-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      {helper && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{helper}</p>
      )}
    </div>
  );
}

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h4>
      {description && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      )}
    </div>
  );
}

function SectionBadge({ value }: { value: string | number }) {
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
      {value}
    </span>
  );
}

export function IncidentGateConfigEditor({
  config: rawConfig,
  onChange,
  endpointType,
  disabled = false,
  isSystemDefault = false,
  hideToggle,
}: IncidentGateConfigEditorProps) {
  const defaultConfig = useMemo(() => getDefaultIncidentGateConfig(endpointType), [endpointType]);
  const config = useMemo<IncidentGateConfig>(() => ({
    ...defaultConfig,
    ...(rawConfig ?? {}),
    resolveActions: rawConfig?.resolveActions ?? defaultConfig.resolveActions ?? [],
    bypassForPriorities: rawConfig?.bypassForPriorities ?? defaultConfig.bypassForPriorities ?? [],
  }), [defaultConfig, rawConfig]);
  const isStepEnabled = (config as IncidentGateConfig & { isEnabled?: boolean }).isEnabled ?? true;
  const [resolveActionsText, setResolveActionsText] = useState(config.resolveActions.join(', '));
  const [prevResolveActions, setPrevResolveActions] = useState(config.resolveActions);
  if (config.resolveActions !== prevResolveActions) {
    setPrevResolveActions(config.resolveActions);
    setResolveActionsText(config.resolveActions.join(', '));
  }

  const [bypassPrioritiesText, setBypassPrioritiesText] = useState(
    config.bypassForPriorities.join(', '),
  );
  const [prevBypassPriorities, setPrevBypassPriorities] = useState(config.bypassForPriorities);
  if (config.bypassForPriorities !== prevBypassPriorities) {
    setPrevBypassPriorities(config.bypassForPriorities);
    setBypassPrioritiesText(config.bypassForPriorities.join(', '));
  }

  const updateConfig = useCallback(
    (patch: Partial<IncidentGateConfig>) => {
      onChange({ ...config, ...patch });
    },
    [config, onChange],
  );

  const showHoldDuration = config.holdStrategy === 'hold_for_resolve' || config.holdStrategy === 'both';
  const showOccurrenceThreshold = config.holdStrategy === 'occurrence_only' || config.holdStrategy === 'both';

  return (
    <div className="space-y-6">
      <StepConfigHeader
        title="Incident Gate"
        isEnabled={isStepEnabled}
        onToggleEnabled={(enabled) =>
          onChange({ ...config, isEnabled: enabled } as IncidentGateConfig & { isEnabled: boolean })
        }
        isSystemDefault={isSystemDefault}
        disabled={disabled}
        hideToggle={hideToggle}
      />
      <div className={isStepEnabled ? 'space-y-6' : 'space-y-6 opacity-50 pointer-events-none'}>
      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" aria-label="Incident gate strategy">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
            <span>⚙️ Strategy</span>
          <SectionBadge value={config.holdStrategy} />
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
          <SectionHeader
              title="Strategy"
            description="Choose how alerts are held before creating incidents."
          />
          <StrategySelector
            options={HOLD_STRATEGY_OPTIONS}
            value={config.holdStrategy}
            onChange={(v) => updateConfig({ holdStrategy: v })}
            disabled={disabled}
          />
        </div>
      </details>

      {showHoldDuration && (
        <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
            <span>⏱️ Hold Duration</span>
            <SectionBadge value={`${config.holdDurationMinutes}m`} />
          </summary>
          <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
            <SectionHeader
              title="Hold Duration"
              description="How long to wait for a matching resolved event before creating the incident."
            />
            <div className="space-y-3">
              <div>
                <FieldLabel
                  label="Hold Duration"
                  helper="Alert will be held for this duration. If a resolved event arrives, the incident is never created."
                  htmlFor="holdDuration"
                />
                <TimeWindowInput
                  value={config.holdDurationMinutes}
                  onChange={(v) => updateConfig({ holdDurationMinutes: v })}
                  min={1}
                  max={config.maxHoldDurationMinutes}
                  disabled={disabled}
                />
              </div>
              <div>
                <FieldLabel
                  label="Maximum Hold Duration"
                  helper="Hard cap on hold duration (safety limit)."
                  htmlFor="maxHoldDuration"
                />
                <TimeWindowInput
                  value={config.maxHoldDurationMinutes}
                  onChange={(v) => updateConfig({ maxHoldDurationMinutes: v })}
                  min={1}
                  max={1440}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        </details>
      )}

      {showOccurrenceThreshold && (
        <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
            <span>🔢 Occurrence Threshold</span>
            <SectionBadge value={`${config.occurrenceThreshold}/${config.occurrenceWindowMinutes}m`} />
          </summary>
          <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
            <SectionHeader
              title="Occurrence Threshold"
              description="Require multiple alert occurrences before creating an incident."
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel
                  label="Minimum Occurrences"
                  helper="Number of alerts required before incident creation."
                  htmlFor="occurrenceThreshold"
                />
                <input
                  id="occurrenceThreshold"
                  type="number"
                  min={2}
                  max={100}
                  value={config.occurrenceThreshold}
                  onChange={(e) => updateConfig({ occurrenceThreshold: Math.max(2, parseInt(e.target.value) || 2) })}
                  disabled={disabled}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <FieldLabel
                  label="Time Window"
                  helper="Occurrences must happen within this window."
                  htmlFor="occurrenceWindow"
                />
                <TimeWindowInput
                  value={config.occurrenceWindowMinutes}
                  onChange={(v) => updateConfig({ occurrenceWindowMinutes: v })}
                  min={1}
                  max={1440}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        </details>
      )}

      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>✅ Resolve Detection</span>
          <SectionBadge value={config.resolveActions.length} />
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
          <SectionHeader
            title="Resolve Detection"
            description="Which values of context.MonitorCondition should cancel a pending hold. This field is set during the Normalization step from your configured source field (e.g., data.essentials.monitorCondition for Azure Monitor)."
          />
          <div>
            <FieldLabel
              label="Resolve Actions"
              helper="Comma-separated list of MonitorCondition values that cancel holds (e.g., Resolved, Closed)."
              htmlFor="resolveActions"
            />
            <input
              id="resolveActions"
              type="text"
              value={resolveActionsText}
              onChange={(e) => setResolveActionsText(e.target.value)}
              onBlur={() => {
                const parsedResolveActions = resolveActionsText
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean);
                setResolveActionsText(parsedResolveActions.join(', '));
                updateConfig({ resolveActions: parsedResolveActions });
              }}
              disabled={disabled}
              placeholder="Resolved, Closed"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>
      </details>

      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>⚡ Priority Bypass</span>
          <SectionBadge value={config.bypassForPriorities.length} />
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
          <SectionHeader
            title="Priority Bypass"
            description="High-priority alerts can bypass the gate entirely for immediate incident creation. Priority values are configured per tenant/customer in the Normalization step."
          />
          <div>
            <FieldLabel
              label="Bypass for Priorities"
              helper="Comma-separated list of priorities that skip the gate (e.g., P1, P2). These match context.Priority set by the Normalization step."
              htmlFor="bypassPriorities"
            />
            <input
              id="bypassPriorities"
              type="text"
              value={bypassPrioritiesText}
              onChange={(e) => setBypassPrioritiesText(e.target.value)}
              onBlur={() => {
                const parsedBypassPriorities = bypassPrioritiesText
                  .split(',')
                  .map((s) => s.trim().toUpperCase())
                  .filter(Boolean);
                setBypassPrioritiesText(parsedBypassPriorities.join(', '));
                updateConfig({ bypassForPriorities: parsedBypassPriorities });
              }}
              disabled={disabled}
              placeholder="P1, P2"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>
      </details>

      {/* Info box */}
      <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3">
        <div className="flex items-start gap-2">
          <span className="text-blue-500 mt-0.5">ℹ️</span>
          <div className="text-xs text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">How the Incident Gate works</p>
            <ul className="list-disc ml-4 space-y-0.5">
              <li>When an alert fires, it&apos;s placed on hold instead of creating an incident immediately.</li>
              <li>If a matching <strong>resolved</strong> event arrives during the hold window, the incident is never created.</li>
              <li>If the hold expires without a resolve, the incident is created automatically.</li>
              <li>Uses the <strong>correlation key</strong> from the Correlation step to match fired and resolved events.</li>
            </ul>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
